import { Alert } from '@mui/material'
import DocLink from '../../components/DocLink'
import planAllowances from '../../../helpers/planAllowances.json'

// Base-location caps come from the plan catalogue mirror, never typed here.
const baseCaps = Object.fromEntries(
  Object.entries(planAllowances.plans).map(([key, plan]) => [
    key,
    plan.allowances.baseLocations,
  ]),
)

// Ground truth for this file is the server's route guards
// (server/src/helpers/permissions.js + each router). Rows describe the
// permission a user needs for the action to succeed end-to-end in the UI.
// Site Admins bypass every team/role check below; MFA-marked actions also
// require the user (including Site Admins) to complete a step-up MFA check.

export default [
  {
    title: 'System & Site Administration',
    rows: [
      {
        action: 'Site Configuration',
        roles: ['Site Admin'],
        note: 'Individual configuration sections also open for the team that owns them (e.g. Governance, Personnel, Fleet, Event Planning, PTS Co-Ordinators).',
      },
      { action: 'Add or Remove Site Admins', roles: ['Site Admin'] },
      { action: 'View the Activity Log', roles: ['Site Admin'] },
      {
        action: 'Getting Started / Onboarding Checklist',
        roles: ['Site Admin'],
      },
      {
        action: 'Edit Base Locations',
        roles: ['Site Admin'],
        note: `Creating a new base is currently open to any signed-in user (within your plan cap: Core ${baseCaps.core}, Premium ${baseCaps.premium}, Enterprise ${baseCaps.enterprise}); editing an existing base needs the Site Admin.`,
      },
      {
        action: 'Subscription, Billing & SMS Credits',
        roles: ['Site Admin'],
      },
      {
        action: 'Manage Access (teams, PTS Co-Ordinator & CAD levels)',
        roles: ['Personnel'],
        note: 'Site Configuration → Employees → Manage Access — one matrix to set teams, the PTS Co-Ordinator flag, and the Command & Control level for anyone except yourself (your own access can only be changed by a Site Admin).',
      },
      {
        action: 'Assign Command & Control Roles to Staff',
        roles: ['Personnel'],
        note: 'Assigned via Manage Access (Site Configuration → Employees). The dedicated CAD Access configuration page is Site Admin only.',
      },
      {
        action: 'Assign Patient Transport Access to Staff',
        roles: ['Personnel'],
        note: 'The PTS Co-Ordinator flag is assigned via Manage Access (Site Configuration → Employees).',
      },
      {
        action: 'Publish Announcements',
        roles: ['Manager', 'Supervisor'],
        note: 'Editing or deleting an existing announcement needs Governance.',
      },
      { action: 'Edit or Delete Announcements', roles: ['Governance'] },
      {
        action: 'Create & Edit Crew Vault Cards',
        roles: ['Governance', 'Manager'],
        note: 'Everyone can read the Crew Vault.',
      },
    ],
  },
  {
    title: 'Dashboards',
    rows: [
      {
        action: 'Home Screen Dashboard & On Call Now',
        roles: ['Any signed-in user'],
        note: 'On Call Now visibility follows the organisation setting (everyone / managers & supervisors / hidden).',
      },
      {
        action: 'Global Overview (management dashboard)',
        roles: ['Manager', 'Supervisor'],
      },
      {
        action: 'Workforce Dashboard & Action Centre',
        roles: ['Personnel'],
      },
      {
        action: 'KPI Dashboard (overview)',
        roles: ['Governance', 'Manager', 'Supervisor'],
      },
      {
        action: 'KPI Drill-down & Setting KPI Targets',
        roles: ['Governance'],
      },
      {
        action: 'View the Wallboard (signed in)',
        roles: ['Manager', 'Supervisor'],
        note: 'Premium plans and above. Standalone TV displays instead use a device token — see the next two rows.',
      },
      {
        action: 'Wallboard Settings (screens, widgets & devices)',
        roles: ['Site Admin'],
      },
      {
        action: 'Authorise or Revoke a Wallboard Display',
        roles: ['Site Admin'],
        note: 'Authorising a TV needs the Site Admin’s username and password (plus their authenticator code if MFA is set up) — passkey-only accounts can’t unlock displays. No user is left signed in on the device.',
      },
      {
        action: 'Send a Shoutout',
        roles: ['Any signed-in user'],
        note: 'From the avatar menu; 280 characters, and not to yourself.',
      },
    ],
  },
  {
    title: 'Employees & HR',
    rows: [
      {
        action: 'View, Create & Edit Employee Records',
        roles: ['Personnel'],
      },
      {
        action: 'Archive Employees & Disable Logins',
        roles: ['Personnel'],
        note: 'Marking someone a Leaver does not block their login by itself — also disable the Login toggle.',
      },
      { action: 'Send Welcome Emails', roles: ['Personnel'] },
      { action: 'DBS Checks & DBS Dashboard', roles: ['Personnel'] },
      { action: 'Driving Licence Checks', roles: ['Personnel'] },
      {
        action: 'Employee Documents & HR Notes',
        roles: ['Personnel'],
      },
      { action: 'ID Cards', roles: ['Personnel'] },
      { action: 'Manage Subcontractors', roles: ['Personnel'] },
      {
        action: 'Training Matrix, Compliance & Expiry Chasing',
        roles: ['Personnel', 'Governance'],
        note: 'The org-wide matrix, reminders and chasing allow Personnel or Governance. Anyone can add their own training records; deleting records needs Personnel.',
      },
      {
        action: 'Open Employee Profiles from Global Search',
        roles: ['Personnel', 'Manager'],
        note: 'Everyone can find staff by name in ⌘K search; only Personnel and Manager results link through to the employee record.',
      },
      {
        action: 'Define Mandatory Training Courses',
        roles: ['Governance'],
      },
      {
        action: 'Review Appraisals',
        roles: ['Personnel', 'Manager', 'Supervisor'],
        note: 'Everyone completes their own appraisal steps on My Appraisals.',
      },
      {
        action: 'Review & Request Occupational Health Screenings',
        roles: ['Personnel', 'Manager', 'Supervisor'],
        note: 'Includes the Occupational Health dashboard. Everyone completes their own screening on My Occupational Health.',
      },
      {
        action: 'View & Decide Job Applications',
        roles: ['Personnel'],
        note: 'Requires the Premium subscription tier or above.',
      },
    ],
  },
  {
    title: 'Shifts, Rostering & Payroll',
    rows: [
      {
        action: 'Create & Publish Rosters / Global Roster',
        roles: ['Personnel'],
      },
      {
        action: 'Review Shift Applications & Swaps',
        roles: ['Personnel', 'Event Planning'],
      },
      {
        action: 'Approve Leave & Manage Staff Availability',
        roles: ['Personnel', 'Manager'],
      },
      {
        action: 'Broadcast Available Shifts',
        roles: ['Personnel'],
      },
      { action: 'Verify Worked Shifts', roles: ['Personnel'] },
      {
        action: 'Payroll, Pay Runs & Staff Invoices',
        roles: ['Personnel'],
      },
      {
        action: 'Export a Pay Run as CSV',
        roles: ['Personnel'],
      },
      { action: 'Approve Staff Expense Claims', roles: ['Personnel'] },
      {
        action: 'Approve, Reject & Undo Overtime Claims',
        roles: ['Personnel'],
        note: 'Undo is blocked once the shift has been paid or added to a pay run.',
      },
      {
        action:
          'Clock In/Out, Acknowledge Shifts, Apply for Shifts, Offer Swaps, My Pay',
        roles: ['Any signed-in user'],
      },
    ],
  },
  {
    title: 'Events & Clients',
    rows: [
      {
        action: 'Create, Edit & Delete Events',
        roles: ['Event Planning'],
      },
      {
        action: 'Event Dashboards, Planning & Cost Breakdown',
        roles: ['Event Planning'],
      },
      { action: 'Manage EMP Templates', roles: ['Event Planning'] },
      {
        action: 'Create & Delete Event Shifts',
        roles: ['Event Planning'],
      },
      {
        action: 'Manage Client Organisations & Contacts',
        roles: ['Event Planning'],
      },
      {
        action: 'Update Receiving Hospitals & Trauma Networks',
        roles: ['Event Planning'],
      },
      {
        action: 'Create & Send Quotes / Engagement Letters',
        roles: ['Event Planning'],
      },
      {
        action: 'Approve Client Portal Accounts',
        roles: ['Event Planning'],
      },
      {
        action: 'Submit Event Expenses',
        roles: ['Event Planning'],
        note: 'Approving expense claims needs the Finance team.',
      },
      {
        action: 'View Event Calendar & EMPs',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Per-event CQC Exposure Assessments',
        roles: ['Governance', 'Event Planning'],
      },
      {
        action: 'Client Portal (events, messages, feedback)',
        roles: ['Approved client contact'],
        note: 'External client contacts, once approved by Event Planning.',
      },
    ],
  },
  {
    title: 'Finance & Billing',
    rows: [
      { action: 'View the Invoice List', roles: ['Finance'] },
      { action: 'View the Quote List', roles: ['Finance'] },
      {
        action: 'Create, Edit & Delete Draft Invoices',
        roles: ['Event Planning', 'PTS Co-Ordinator'],
        note: 'Day-to-day invoice drafting sits with the coordinating teams; only Draft invoices can be edited or deleted.',
      },
      {
        action: 'Approve an Invoice / Revert to Draft',
        roles: ['Finance'],
        note: 'An invoice must be approved before it can be sent or marked paid. Reverting to Draft un-approves it and is blocked once payments or credit notes are recorded.',
      },
      {
        action: 'Send Invoice Emails',
        roles: ['Event Planning', 'PTS Co-Ordinator'],
        note: 'Only an approved invoice can be sent.',
      },
      {
        action: 'Mark an Invoice Paid & Reissue Invoices',
        roles: ['Finance'],
      },
      { action: 'Record Payments Against Invoices', roles: ['Finance'] },
      {
        action: 'Edit & Delete Recorded Payments',
        roles: ['Finance'],
      },
      { action: 'Raise Credit Notes', roles: ['Finance'] },
      { action: 'Export Invoices to Xero (CSV)', roles: ['Finance'] },
      { action: 'Approve Event Expense Claims', roles: ['Finance'] },
      {
        action: 'Stock Catalogue & Consumable Cost Reports',
        roles: ['Finance'],
      },
      {
        action: 'Open Event Invoices & Unpaid Invoices',
        roles: ['Event Planning', 'PTS Co-Ordinator'],
        note: 'Invoice detail pages open for Event Planning or PTS Co-Ordinators rather than Finance — staff doing end-to-end invoice work need both teams.',
      },
      {
        action: 'PTS Invoicing, Billing Runs, Credit Notes & Tariffs',
        roles: ['PTS Co-Ordinator'],
        note: 'Patient Transport billing is managed by PTS Co-Ordinators, separately from the Finance team.',
      },
    ],
  },
  {
    title: 'Clinical Records & Safeguarding',
    rows: [
      {
        action: 'Create & Complete Patient Care Records (ePCRs)',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Open Completed Patient Records',
        roles: ['Governance'],
        note: 'Also requires completing the MFA security check — this applies to Site Admins too.',
      },
      {
        action: 'ePCR Governance Dashboard & Record Search',
        roles: ['Governance'],
        note: 'MFA required.',
      },
      {
        action: 'Review Safeguarding Concerns',
        roles: ['Governance'],
        note: 'MFA required. Safeguarding alerts are reviewed from the governance ePCR dashboard; the manager review (notes, referrals, follow-ups) is recorded inside the patient record view.',
      },
      {
        action: 'Mark Safeguarding Follow-ups Complete',
        roles: ['Governance'],
        note: 'MFA required (accessed from within the patient record view).',
      },
      {
        action: 'Create & Edit Paper PCRs',
        roles: ['Governance'],
        note: 'Viewing a paper PCR also requires MFA.',
      },
      {
        action: 'Add Clinical Advice / Audit Notes to Records',
        roles: ['Governance'],
      },
      {
        action: 'Force-transfer Record Ownership',
        roles: ['Governance'],
        note: 'Clinicians can transfer their own records without this.',
      },
      {
        action: 'Issue & Revoke Patient Share Codes',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Review Patient Feedback',
        roles: ['Governance'],
        note: 'The feedback form itself is public — patients do not log in.',
      },
      {
        action: 'Request Imaging from an ePCR',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Imaging Approvals & Worklist',
        roles: ['Configured imaging approver'],
        note: 'Only the named approvers configured in the imaging settings can decide requests or open the worklist — this is a per-person list, not a team.',
      },
      {
        action: 'Radiation Dose Overview',
        roles: ['Personnel'],
        note: 'Per-employee doses also show on the employee record (Radiation Doses tab).',
      },
    ],
  },
  {
    title: 'Clinical Governance',
    rows: [
      {
        action: 'Raise an Incident',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Raise a Speak Up Concern',
        roles: ['Any signed-in user'],
        note: 'Concerns are anonymous by default. Available unless your organisation has disabled Speak Up.',
      },
      {
        action: 'Review Speak Up Concerns',
        roles: ['Governance'],
      },
      {
        action:
          'Investigate Incidents, Witness Statements & Incident Comments',
        roles: ['Governance'],
      },
      {
        action: 'Create Audit Templates & Review Submitted Audits',
        roles: ['Governance'],
        note: 'Anyone can complete and submit an audit assigned to them.',
      },
      { action: 'Manage the Risk Register', roles: ['Governance'] },
      {
        action: 'Publish & Review Policies, SOPs and PGDs',
        roles: ['Governance'],
        note: 'Everyone can read policies/SOPs, confirm reading, and sign PGDs.',
      },
      { action: 'Manage Safety Alerts', roles: ['Governance'] },
      {
        action: 'Record Governance Meetings & Minutes',
        roles: ['Governance'],
      },
      { action: 'Manage Website Enquiries', roles: ['Governance'] },
    ],
  },
  {
    title: 'Medicines Management',
    rows: [
      {
        action: 'Record a Medicine Administration',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Correct or Void an Administration Record',
        roles: ['Governance'],
        note: 'Voiding a controlled drug administration also needs a witness.',
      },
      {
        action: 'View the Administrations Register',
        roles: ['Governance'],
      },
      {
        action: 'Manage Medicine Stock (add, discard, dispose, archive)',
        roles: ['Governance'],
      },
      {
        action: 'Drug Bags: assign drugs, report broken seals, bag audits',
        roles: ['Any signed-in user'],
      },
      { action: 'Review Medicines Audits', roles: ['Governance'] },
      {
        action: 'Controlled Drug Catalogue & Staff CD Audits',
        roles: ['Governance'],
      },
      {
        action: 'Configure Medicines & Locations',
        roles: ['Governance'],
      },
    ],
  },
  {
    title: 'CQC Centre & Registration',
    rows: [
      {
        action: 'CQC Dashboard, Evidence, Gaps & Actions',
        roles: ['Governance'],
      },
      { action: 'Generate the CQC Inspection Pack', roles: ['Governance'] },
      {
        action: 'CQC Settings & Quality Statement Responses',
        roles: ['Governance'],
      },
      {
        action: 'Regulated Activities & Statement of Purpose',
        roles: ['Governance'],
      },
      {
        action: 'Per-event CQC Exposure Assessments',
        roles: ['Governance', 'Event Planning'],
      },
    ],
  },
  {
    title: 'Fleet & Equipment',
    rows: [
      {
        action: 'View, Add & Edit Vehicles',
        roles: ['Any signed-in user'],
        note: 'Archived vehicles are restricted to the Fleet team.',
      },
      {
        action: 'Report a Defect',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Defect List & Defect Management',
        roles: ['Fleet', 'Make Ready', 'Governance'],
      },
      {
        action: 'Perform Cleans, VDIs & Safety Checks',
        roles: ['Any signed-in user'],
      },
      {
        action: 'Configure Cleaning Schedules & VDI Options',
        roles: ['Make Ready'],
      },
      {
        action: 'Cleaning & Compliance Dashboards',
        roles: ['Governance', 'Make Ready', 'Fleet'],
      },
      {
        action: 'Create & Edit Assets',
        roles: ['Fleet', 'Make Ready'],
        note: 'Deleting an asset needs Governance. Everyone can view assets.',
      },
      {
        action: 'Maintenance Logs',
        roles: ['Fleet', 'Make Ready'],
        note: 'Adding a maintenance log entry needs Governance.',
      },
      {
        action: 'Define Load List Standards',
        roles: ['Governance', 'Manager', 'Supervisor'],
      },
      {
        action: 'Make Ready Oversight (Overview, Restock Queue, History)',
        roles: ['Fleet', 'Make Ready', 'Governance', 'Manager', 'Supervisor'],
        note: 'Completing a Make Ready check is open to any crew member.',
      },
    ],
  },
  {
    title: 'Command & Control',
    topComponent: (
      <Alert severity="info" variant="outlined" sx={{ borderRadius: 0 }}>
        Command &amp; Control uses its own role tiers (Viewer → Call Taker /
        Dispatcher → Commander). Personnel assign them per employee in{' '}
        <DocLink
          text="Configuration -> Employees -> Manage Access"
          url="/site-configuration/manage-access"
        />
        ; the dedicated CAD Access configuration page is Site Admin only. Call
        Takers and Dispatchers currently share the same permission level.
      </Alert>
    ),
    rows: [
      {
        action: 'Open, Close & Reopen Control Rooms',
        roles: ['Commander'],
      },
      { action: 'Upload & Set Event Maps', roles: ['Commander'] },
      { action: 'Create Treatment Centres', roles: ['Commander'] },
      {
        action: 'Log Complex Decisions & Tactical Log Entries',
        roles: ['Commander'],
        note: 'All CAD roles can read complex decisions.',
      },
      {
        action: 'Create & Update Incidents / Call Triage',
        roles: ['Call Taker', 'Dispatcher', 'Commander'],
      },
      {
        action: 'Add Control Room & Incident Log Entries',
        roles: ['Call Taker', 'Dispatcher', 'Commander'],
      },
      {
        action:
          'Manage Resources (create, assign, move, update times, crew changes)',
        roles: ['Call Taker', 'Dispatcher', 'Commander'],
      },
      {
        action: 'Message Resources (dispatch chat)',
        roles: ['Call Taker', 'Dispatcher', 'Commander'],
      },
      {
        action:
          'View Control Rooms, Incidents, Logs & Chats (read-only, with View Only badge)',
        roles: ['Viewer', 'Call Taker', 'Dispatcher', 'Commander'],
      },
      {
        action: 'Author & Approve Call Triage Protocols',
        roles: ['Governance'],
        note: 'Protocol authoring is a governance function, not a CAD tier.',
      },
      {
        action:
          'Crew App: acknowledge taskings, update status, send location',
        roles: ['Any signed-in user'],
        note: 'Scoped to the resource the crew member is assigned to.',
      },
    ],
  },
  {
    title: 'Patient Transport',
    topComponent: (
      <Alert severity="info" variant="outlined" sx={{ borderRadius: 0 }}>
        Patient Transport has one access level: <strong>Co-Ordinator</strong>,
        assigned per employee by Personnel in{' '}
        <DocLink
          text="Configuration -> Employees -> Manage Access"
          url="/site-configuration/manage-access"
        />
        . There is no separate crew level — the crew screens (My PTS Duty,
        journey statuses, chat) are open to every employee, scoped to the
        duties they are rostered on.
      </Alert>
    ),
    rows: [
      {
        action: 'Search, Edit & Cancel Bookings and Journeys',
        roles: ['PTS Co-Ordinator'],
      },
      {
        action: 'Plan Duties & Routes / Assign Journeys',
        roles: ['PTS Co-Ordinator'],
      },
      {
        action: 'PTS Control Room & Dispatch Chat',
        roles: ['PTS Co-Ordinator'],
      },
      {
        action: 'Create & Edit Patients / Patient Lookup',
        roles: ['PTS Co-Ordinator'],
        note: 'MFA required — this applies to Site Admins too.',
      },
      {
        action: 'Patient Risk Assessments',
        roles: ['PTS Co-Ordinator'],
        note: 'MFA required.',
      },
      {
        action: 'Manage Tariffs & Organisation Contracts',
        roles: ['PTS Co-Ordinator'],
      },
      {
        action: 'PTS Invoicing, Quotes & Billing Runs',
        roles: ['PTS Co-Ordinator'],
      },
      {
        action: 'Manage PTS Organisations & Hospital Portal Users',
        roles: ['PTS Co-Ordinator'],
      },
      {
        action: 'Reports, Patient Timelines & Vehicle Timelines',
        roles: ['PTS Co-Ordinator'],
      },
      {
        action: 'Crew View & Mobile App (duty, journey statuses, chat)',
        roles: ['Any employee'],
        note: 'Scoped to the duties the employee is rostered on. Subcontractor-only and portal accounts are excluded.',
      },
      {
        action: 'Hospital Portal (book, track, quotes, invoices)',
        roles: ['Hospital portal user'],
        note: 'External accounts scoped to their own organisation, managed by PTS Co-Ordinators.',
      },
      {
        action: 'Public Journey Tracking Link',
        roles: ['No login needed'],
        note: 'Secure tokenised link shared per journey.',
      },
    ],
  },
]
